/**
 * The renderer: resolved items in, one detached menu element out.
 *
 * This is the only module in the core that creates DOM. It evaluates no
 * predicates, computes no coordinates and wires up no events: it is handed a
 * list whose every conditional field is already a plain value, and it returns
 * an element that the caller positions, shows and listens to.
 *
 * The markup it produces is a contract. The stylesheet, the keyboard layer and
 * every end to end selector are written against these exact class names, roles
 * and attributes, so a change here is a change to all three.
 *
 * Text safety: `label`, `hint`, the header and an error message are always set
 * with `textContent`. The only fields that reach `innerHTML` are `icon` and the
 * string form of a `custom` item's `render`, both documented as trusted input.
 */

import type { Ctx, Meta, ResolvedItem } from './types'

export interface RenderOptions {
  header?: string
  class?: string[]
  variant?: string
  /** Used for `aria-controls` on the trigger and `aria-activedescendant`. */
  id: string
  /**
   * A `custom` item carries an unevaluated `render(meta, ctx)`, so these are
   * needed to render one. They are optional because every other kind renders
   * without them; a custom item met without them is skipped with a warning
   * rather than rendered half way.
   */
  meta?: Meta
  ctx?: Ctx
  /**
   * Called when a `custom` item's `render` throws. The row is skipped and the
   * rest of the menu is built as usual.
   *
   * `ContextMenu` passes one that emits `error`, so a throwing `render` is
   * reported on the same channel as a throwing items resolver instead of
   * escaping `open()` as an unhandled rejection and leaving no menu at all.
   */
  onError?: (error: unknown, item: ResolvedItem) => void
}

/** Rows that render as a button. The three kinds share every visual part. */
type RowItem = Extract<ResolvedItem, { kind: 'item' | 'checkbox' | 'radio' }>

/** Counter behind the ids of the transient loading and error roots. */
let transientCount = 0

function element<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)

  node.className = className

  return node
}

/** Add class names, ignoring the empty and whitespace laden entries. */
function addClasses(node: HTMLElement, classes: readonly string[] | undefined): void {
  if (classes === undefined) return

  for (const entry of classes) {
    for (const name of entry.split(/\s+/)) {
      if (name.length > 0) node.classList.add(name)
    }
  }
}

/**
 * The icon rule, in one sentence: an `icon` containing a `<` is inserted as
 * trusted HTML, and anything else is treated as a space separated class list
 * added to the icon span.
 *
 * That covers both real cases with no extra option. `<svg>...</svg>` is markup;
 * `fa fa-trash` or `material-icons` is a class name for an icon font.
 */
function renderIcon(icon: string): HTMLElement {
  const span = element('span', 'ctx-item-icon')

  span.setAttribute('aria-hidden', 'true')

  if (icon.includes('<')) {
    span.innerHTML = icon
  } else {
    addClasses(span, [icon])
  }

  return span
}

function renderHeader(label: string): HTMLElement {
  const header = element('div', 'ctx-header')

  header.setAttribute('role', 'presentation')
  header.textContent = label

  return header
}

function renderSeparator(): HTMLElement {
  const separator = element('div', 'ctx-separator')

  separator.setAttribute('role', 'separator')

  return separator
}

function roleFor(item: RowItem): string {
  if (item.kind === 'checkbox') return 'menuitemcheckbox'
  if (item.kind === 'radio') return 'menuitemradio'

  return 'menuitem'
}

/**
 * One row.
 *
 * A disabled row keeps `aria-disabled` and never the `disabled` attribute, so
 * it stays focusable and a screen reader can announce why it cannot be used.
 * The check mark of a checkbox or radio is drawn by the stylesheet from
 * `aria-checked`, which keeps the markup identical across all three kinds.
 */
function renderRow(item: RowItem, index: number): HTMLElement {
  const button = element('button', 'ctx-item')

  button.type = 'button'
  button.setAttribute('role', roleFor(item))
  button.tabIndex = -1
  button.setAttribute('aria-disabled', String(item.disabled))
  button.dataset.ctxIndex = String(index)

  if (item.variant !== undefined) button.dataset.variant = item.variant
  if (item.id !== undefined) button.dataset.ctxItemId = item.id

  addClasses(button, item.class)

  if (item.kind !== 'item') button.setAttribute('aria-checked', String(item.checked))
  if (item.kind === 'radio') button.dataset.ctxGroup = item.group

  const hasSubmenu = item.kind === 'item' && item.hasSubmenu

  if (hasSubmenu) {
    button.setAttribute('aria-haspopup', 'menu')
    button.setAttribute('aria-expanded', 'false')
  }

  if (item.icon !== undefined) button.append(renderIcon(item.icon))

  const label = element('span', 'ctx-item-label')

  label.textContent = item.label
  button.append(label)

  if (item.hint !== undefined) {
    const hint = element('span', 'ctx-item-hint')

    hint.setAttribute('aria-hidden', 'true')
    hint.textContent = item.hint
    button.append(hint)
  }

  if (hasSubmenu) {
    const arrow = element('span', 'ctx-item-arrow')

    arrow.setAttribute('aria-hidden', 'true')
    button.append(arrow)
  }

  return button
}

function renderCustom(
  item: Extract<ResolvedItem, { kind: 'custom' }>,
  opts: RenderOptions,
): HTMLElement | null {
  const { meta, ctx } = opts

  if (meta === undefined || ctx === undefined) {
    console.warn('[context-menu] a custom item needs meta and ctx to render, skipping it')

    return null
  }

  let result: HTMLElement | string

  // `render` is consumer code, called while the menu is being built inside an
  // async `open()`. Unguarded, a throw rejects that promise: no menu appears,
  // no event is emitted, and the page logs an unhandled rejection on every
  // right click. Failing closed matches `resolveItems`, which drops an item
  // whose predicates throw rather than taking the whole menu down with it.
  try {
    result = item.render(meta, ctx)
  } catch (error) {
    if (opts.onError !== undefined) opts.onError(error, item)
    else console.error('[context-menu] a custom item failed to render, skipping it', error)

    return null
  }

  const wrapper = element('div', 'ctx-custom')

  wrapper.setAttribute('role', 'presentation')

  if (typeof result === 'string') {
    // Documented as trusted input, exactly like `icon`.
    wrapper.innerHTML = result
  } else {
    wrapper.append(result)
  }

  return wrapper
}

function renderItem(item: ResolvedItem, index: number, opts: RenderOptions): HTMLElement | null {
  switch (item.kind) {
    case 'separator':
      return renderSeparator()
    case 'header':
      return renderHeader(item.label)
    case 'custom':
      return renderCustom(item, opts)
    default:
      return renderRow(item, index)
  }
}

/** The shared root, so a real menu and a transient one style identically. */
function renderRoot(id: string): HTMLElement {
  const menu = element('div', 'ctx-menu')

  menu.setAttribute('popover', 'manual')
  menu.setAttribute('role', 'menu')
  menu.id = id

  return menu
}

/**
 * Build the menu element for one open.
 *
 * The element is detached: the caller appends it, shows the popover and
 * positions it. `data-ctx-index` on each row is the item's index in `items`,
 * which is how a click is mapped back to the `ResolvedItem` that produced it.
 */
export function renderMenu(items: ResolvedItem[], opts: RenderOptions): HTMLElement {
  const menu = renderRoot(opts.id)

  addClasses(menu, opts.class)

  if (opts.variant !== undefined) menu.dataset.variant = opts.variant
  if (opts.header !== undefined && opts.header !== '') menu.append(renderHeader(opts.header))

  items.forEach((item, index) => {
    const node = renderItem(item, index, opts)

    if (node !== null) menu.append(node)
  })

  return menu
}

/**
 * The placeholder shown while an async `items` resolver is pending.
 *
 * It is a complete menu root rather than a row, so the caller can show and
 * place it with the same code path it uses for a real menu, then swap the
 * element once the items arrive.
 */
export function renderLoading(): HTMLElement {
  transientCount += 1

  const menu = renderRoot(`ctx-menu-loading-${transientCount}`)

  menu.dataset.state = 'loading'
  menu.setAttribute('aria-busy', 'true')

  const row = element('div', 'ctx-loading')

  row.setAttribute('role', 'status')
  row.textContent = 'Loading'
  menu.append(row)

  return menu
}

/** The menu shown when resolving items failed. A complete root, as above. */
export function renderError(message: string): HTMLElement {
  transientCount += 1

  const menu = renderRoot(`ctx-menu-error-${transientCount}`)

  menu.dataset.state = 'error'

  const row = element('div', 'ctx-error')

  row.setAttribute('role', 'alert')
  row.textContent = message
  menu.append(row)

  return menu
}
